import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Button, Flex, Pane, Text, Toast } from '@leverege/ui-elements';
import QRCode from 'qrcode';
import I18N from '@leverege/i18n';
import { Duration } from 'luxon';
import useRequestDeviceCode from "./hooks/useRequestDeviceCode.js";
import S from './Login.css';
import LogoComponent from "./views/LogoComponent.js";
const {
  t
} = I18N.ns('login.RequestDeviceCode');
const INVALID_CODES = new Set(['incorrect_device_code', 'expired_token']);
function RequestDeviceCode(props) {
  const {
    classNames: {
      className
    },
    variants: {
      variant
    },
    onBackToLogin,
    onRequestDeviceCode,
    onRequestDeviceAccessToken,
    deviceCodeRequest,
    deviceAccessTokenRequest
  } = useRequestDeviceCode(props);
  const [qrCodeSrc, setQRCodeSrc] = useState();
  const [hasAccessTokenError, setHasAccessTokenError] = useState(false);
  const [hasDeviceCodeError, setHasDeviceCodeError] = useState(false);
  useEffect(() => {
    const fetch = async () => {
      await onRequestDeviceCode();
    };
    fetch();
  }, []);
  useEffect(() => {
    let intervalId;
    if (deviceCodeRequest?.deviceCode && !hasAccessTokenError) {
      intervalId = setInterval(() => {
        onRequestDeviceAccessToken();
      }, deviceCodeRequest.interval ? deviceCodeRequest.interval * 1000 : 5000);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [deviceCodeRequest, onRequestDeviceAccessToken, hasAccessTokenError]);
  useEffect(() => {
    if (deviceCodeRequest?.authorizationUriComplete) {
      QRCode.toDataURL(deviceCodeRequest.authorizationUriComplete, {
        width: 300,
        margin: 2,
        color: {
          light: window.getComputedStyle(document.documentElement).getPropertyValue('--color-brand025') || '#ebf1fd'
        }
      }).then(src => {
        setQRCodeSrc(src);
      });
    }
  }, [deviceCodeRequest?.authorizationUriComplete]);
  useEffect(() => {
    if (!deviceAccessTokenRequest?.loading) {
      if (deviceAccessTokenRequest?.error) {
        if (INVALID_CODES.has(deviceAccessTokenRequest.error.exception.code)) {
          setHasAccessTokenError(true);
          return;
        }
      }
      setHasAccessTokenError(false);
    }
  }, [deviceAccessTokenRequest]);
  useEffect(() => {
    if (!deviceCodeRequest?.loading && deviceCodeRequest?.error) {
      setHasDeviceCodeError(true);
      return;
    }
    setHasDeviceCodeError(false);
  }, [deviceCodeRequest]);
  const userCode = useMemo(() => {
    if (!deviceCodeRequest?.userCode) {
      return null;
    }
    return deviceCodeRequest.userCode;
  }, [deviceCodeRequest]);
  const onCopyLink = useCallback(() => {
    const copyLink = async () => {
      await navigator.clipboard.writeText(deviceCodeRequest?.authorizationUri);
      Toast.success('Sign In Code Copied to Clipboard');
    };
    copyLink();
  }, [deviceCodeRequest?.authorizationUri]);
  return /*#__PURE__*/React.createElement(Pane, {
    variant: variant,
    className: classnames(className, S.pane),
    layout: "flex:colMCenter"
  }, /*#__PURE__*/React.createElement(LogoComponent, {
    variant: "loginLogo"
  }), /*#__PURE__*/React.createElement(Flex, {
    variant: "loginPane",
    className: S.loginPaneFlex
  }, /*#__PURE__*/React.createElement(QRCodeView, {
    deviceCodeError: deviceCodeRequest?.error?.exception,
    hasAccessTokenError: hasAccessTokenError,
    hasDeviceCodeError: hasDeviceCodeError,
    qrCodeSrc: qrCodeSrc
  }), !hasDeviceCodeError && /*#__PURE__*/React.createElement(Pane, {
    variant: "loginPaneHalf",
    layout: "flex:colLCenterMiddle"
  }, hasAccessTokenError ? /*#__PURE__*/React.createElement(Flex, {
    variant: "colSCenter"
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "qrCodeTitle|formTitleCenter"
  }, t('codeExpired')), /*#__PURE__*/React.createElement(Text, {
    variant: "qrCodeInstruction|formBodyCenter"
  }, t('codeExpiredInstruction')), /*#__PURE__*/React.createElement(Button, {
    variant: "primaryFullWidth",
    onClick: () => onRequestDeviceCode()
  }, t('generateNewCode'))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
    variant: "colSCenter"
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "qrCodeInstruction|formBodyCenter",
    dangerouslySetInnerHTML: {
      __html: t('instructions')
    }
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "link",
    title: "Copy Link to Clipboard",
    onClick: onCopyLink
  }, deviceCodeRequest?.authorizationUri?.replace('https://', ''))), /*#__PURE__*/React.createElement(Flex, {
    variant: "colSCenter"
  }, /*#__PURE__*/React.createElement(Text, {
    variant: "formBodyCenter"
  }, t('confirmCode')), /*#__PURE__*/React.createElement(Text, {
    variant: "deviceCode"
  }, userCode), /*#__PURE__*/React.createElement(ExpiresIn, {
    expiresIn: deviceCodeRequest?.expiresIn
  }))))), /*#__PURE__*/React.createElement(Button, {
    type: "button",
    onClick: onBackToLogin,
    variant: "link"
  }, t('backText')));
}
function QRCodeView({
  deviceCodeError,
  hasAccessTokenError,
  hasDeviceCodeError,
  qrCodeSrc
}) {
  if (hasDeviceCodeError) {
    return /*#__PURE__*/React.createElement(Pane, {
      variant: "loginPaneHalf",
      layout: "flex:colMCenter"
    }, /*#__PURE__*/React.createElement(Pane, {
      variant: "deviceQRCodeError",
      layout: "flex:colCenterMiddle",
      className: S.qrCode
    }, /*#__PURE__*/React.createElement(Text, {
      variant: "title"
    }, t('deviceCodeError')), /*#__PURE__*/React.createElement(Text, {
      variant: "bodySmallBold",
      style: {
        textAlign: 'center'
      }
    }, deviceCodeError?.message)));
  }
  return /*#__PURE__*/React.createElement(Pane, {
    variant: "loginPaneHalf",
    layout: "flex:colMCenter"
  }, hasAccessTokenError ? /*#__PURE__*/React.createElement("img", {
    src: "https://storage.googleapis.com/molten-ui-assets/no-data/qrcode-placeholder.png",
    alt: "qr coe placeholder",
    style: {
      maxWidth: '90%'
    }
  }) : /*#__PURE__*/React.createElement(Pane, {
    variant: "deviceQRCode",
    layout: "flex:colCenter",
    className: S.qrCode
  }, qrCodeSrc && /*#__PURE__*/React.createElement("img", {
    src: qrCodeSrc,
    alt: "login qr code",
    style: {
      maxWidth: '90%'
    }
  })));
}
function ExpiresIn({
  expiresIn,
  userCode
}) {
  const [elapsedSecs, setElapsedSecs] = useState(0);
  useEffect(() => {
    setElapsedSecs(0);
  }, [userCode]);
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSecs(elapsedSecs + 1);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [elapsedSecs]);
  const remainingSecs = Math.max(0, (expiresIn || 300) - elapsedSecs);
  return /*#__PURE__*/React.createElement(Text, {
    variant: "expiresIn|caption"
  }, "Expires in ", Duration.fromDurationLike({
    seconds: remainingSecs
  }).toFormat('mm:ss'));
}
export default connect(state => {
  return {
    deviceCodeRequest: state.auth?.deviceCodeRequest,
    deviceAccessTokenRequest: state.auth?.deviceAccessTokenRequest
  };
})(RequestDeviceCode);